import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo, Redo, Link as LinkIcon, Image as ImageIcon,
  Minus, Code,
} from 'lucide-react';
import { CaptionedImage, CAPTIONED_IMAGE_CSS } from './CaptionedImage';

const API = process.env.REACT_APP_BACKEND_URL;

const Btn = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-active={active ? 'true' : 'false'}
    className={`px-2 py-1.5 rounded text-sm font-bold transition-colors border ${
      active
        ? 'bg-red-600 border-red-500 text-white'
        : 'bg-zinc-900 border-zinc-800 text-gray-300 hover:bg-zinc-800 hover:text-white'
    } disabled:opacity-30 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange, token, placeholder = 'Write your story…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      CaptionedImage,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-invert prose-red max-w-none min-h-[420px] px-4 py-3 focus:outline-none prose-headings:text-white prose-strong:text-white prose-a:text-red-400 prose-blockquote:border-red-500',
        'data-testid': 'article-editor-content',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange && onChange(ed.getHTML());
    },
  });

  // Sync incoming value updates without wiping the caret every keystroke
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const uploadInlineImage = useCallback(
    async (file) => {
      if (!file) return;
      const presignRes = await fetch(`${API}/api/admin/articles/presign-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type }),
      });
      if (!presignRes.ok) {
        const data = await presignRes.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not prepare image upload.');
      }
      const { upload_url, public_url } = await presignRes.json();
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error('R2 upload failed.');
      // Ask for a caption before inserting so mobile users don't have to
      // scroll to type into the inline caption input.
      const caption = window.prompt('Image caption (optional - leave blank for none):', '') || '';
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'captionedImage',
          attrs: { src: public_url, alt: file.name, caption: caption.trim() },
        })
        .run();
    },
    [editor, token],
  );

  const promptLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (!url) return editor.chain().focus().extendMarkRange('link').unsetLink().run();
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const pickImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          await uploadInlineImage(file);
        } catch (err) {
          alert(err.message);
        }
      }
    };
    input.click();
  };

  if (!editor) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 text-gray-500 text-sm">
        Loading editor…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-red-900/30 bg-zinc-950/70 overflow-visible" data-testid="article-editor">
      <style>{CAPTIONED_IMAGE_CSS}</style>
      <div className="sticky top-[116px] md:top-[72px] z-40 flex flex-wrap items-center gap-1.5 px-2 py-2 border-b border-zinc-800 bg-zinc-900/95 backdrop-blur supports-[backdrop-filter]:bg-zinc-900/85 shadow-[0_6px_14px_-8px_rgba(0,0,0,0.9)] rounded-t-lg">
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo className="h-4 w-4" />
        </Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo className="h-4 w-4" />
        </Btn>
        <span className="w-px h-6 bg-zinc-800 mx-1" />
        <Btn title="Heading 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })}>
          <Heading1 className="h-4 w-4" />
        </Btn>
        <Btn title="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}>
          <Heading2 className="h-4 w-4" />
        </Btn>
        <Btn title="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}>
          <Heading3 className="h-4 w-4" />
        </Btn>
        <span className="w-px h-6 bg-zinc-800 mx-1" />
        <Btn title="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}>
          <Bold className="h-4 w-4" />
        </Btn>
        <Btn title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}>
          <Italic className="h-4 w-4" />
        </Btn>
        <Btn title="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')}>
          <Strikethrough className="h-4 w-4" />
        </Btn>
        <Btn title="Code" onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')}>
          <Code className="h-4 w-4" />
        </Btn>
        <span className="w-px h-6 bg-zinc-800 mx-1" />
        <Btn title="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}>
          <List className="h-4 w-4" />
        </Btn>
        <Btn title="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}>
          <ListOrdered className="h-4 w-4" />
        </Btn>
        <Btn title="Quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}>
          <Quote className="h-4 w-4" />
        </Btn>
        <Btn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="h-4 w-4" />
        </Btn>
        <span className="w-px h-6 bg-zinc-800 mx-1" />
        <Btn title="Insert link" onClick={promptLink} active={editor.isActive('link')}>
          <LinkIcon className="h-4 w-4" />
        </Btn>
        <Btn title="Insert image" onClick={pickImage}>
          <ImageIcon className="h-4 w-4" />
        </Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
